// Factory initial state for URX44V in Standard mode, captured from the device
// after a reset and used to seed a new plan (see defaultPlan). Values are the
// decoded plan-unit defaults the device ships with; the routing wires mirror its
// default input source / send / patch / ducker-key topology. URX44 reuses this
// capture verbatim; URX22 is a remap of it to its channel layout (see initial-urx22.ts).

import type { NodeParams, PlanConnection } from "../core/plan";
import { SSMCS_INITIAL } from "../core/plan";

export const URX44V_NODE_PARAMS: Record<string, NodeParams> = {
  // The input stage the unit keeps per MIC/LINE jack and per digital source.
  "in.micline_1_2": {
    gainL: -8,
    gainR: -8,
    phantomL: false,
    phantomR: false,
    clipSafeL: false,
    clipSafeR: false,
  },
  "in.micline_3_4": {
    gainL: -8,
    gainR: -8,
    phantomL: false,
    phantomR: false,
    clipSafeL: false,
    clipSafeR: false,
    hiZL: false,
    hiZR: false,
  },
  "in.aux": { dGain: 0 },
  "in.sdplay": { dGain: 0 },
  "in.usbmain_a": { dGain: -14 },
  "in.usbmain_b": { dGain: -14 },
  "in.usbmain_c": { dGain: -14 },
  "in.usbdaw_1_2": { dGain: 0 },
  "in.usbdaw_3_4": { dGain: 0 },
  "in.usbdaw_5_6": { dGain: 0 },
  "in.usbdaw_7_8": { dGain: 0 },
  "in.usbdaw_9_10": { dGain: 0 },
  "in.usbdaw_11_12": { dGain: 0 },
  "in.usbsub": { dGain: -14 },
  "in.hdmi": { dGain: 0 },
  ch1: {
    on: true,
    hpf: false,
    hpfFreq: 80,
    compEqType: 0,
    recPoint: 4,
    phase: false,
    gateOn: false,
    compOn: false,
    eqOn: true,
    eqOneKnob: { on: false, type: 0, level: 0 },
    eqBands: [
      { on: true, q: 0.71, freq: 125, gain: 0, type: 1 },
      { on: true, q: 0.71, freq: 1000, gain: 0 },
      { on: true, q: 0.71, freq: 4000, gain: 0 },
      { on: true, q: 0.71, freq: 10000, gain: 0, type: 1 },
    ],
    gate: { threshold: -50, range: -56, attack: 20.17, hold: 15.3, decay: 150.2 },
    comp: {
      threshold: -18,
      ratio: 3,
      gain: 2,
      attack: 34.58,
      release: 218,
      knee: 1,
      autoMakeup: false,
      oneKnob: false,
      oneKnobLevel: 0,
    },
    ssmcs: SSMCS_INITIAL,
    insertFx: -1,
    insertFxOn: false,
    stereoLink: false,
    panBal: 0,
  },
  ch2: {
    on: true,
    hpf: false,
    hpfFreq: 80,
    compEqType: 0,
    recPoint: 4,
    phase: false,
    gateOn: false,
    compOn: false,
    eqOn: true,
    eqOneKnob: { on: false, type: 0, level: 0 },
    eqBands: [
      { on: true, q: 0.71, freq: 125, gain: 0, type: 1 },
      { on: true, q: 0.71, freq: 1000, gain: 0 },
      { on: true, q: 0.71, freq: 4000, gain: 0 },
      { on: true, q: 0.71, freq: 10000, gain: 0, type: 1 },
    ],
    gate: { threshold: -50, range: -56, attack: 20.17, hold: 15.3, decay: 150.2 },
    comp: {
      threshold: -18,
      ratio: 3,
      gain: 2,
      attack: 34.58,
      release: 218,
      knee: 1,
      autoMakeup: false,
      oneKnob: false,
      oneKnobLevel: 0,
    },
    ssmcs: SSMCS_INITIAL,
    insertFx: -1,
    insertFxOn: false,
  },
  ch3: {
    on: true,
    hpf: false,
    hpfFreq: 80,
    compEqType: 0,
    recPoint: 4,
    phase: false,
    gateOn: false,
    compOn: false,
    eqOn: true,
    eqOneKnob: { on: false, type: 0, level: 0 },
    eqBands: [
      { on: true, q: 0.71, freq: 125, gain: 0, type: 1 },
      { on: true, q: 0.71, freq: 1000, gain: 0 },
      { on: true, q: 0.71, freq: 4000, gain: 0 },
      { on: true, q: 0.71, freq: 10000, gain: 0, type: 1 },
    ],
    gate: { threshold: -50, range: -56, attack: 20.17, hold: 15.3, decay: 150.2 },
    comp: {
      threshold: -18,
      ratio: 3,
      gain: 2,
      attack: 34.58,
      release: 218,
      knee: 1,
      autoMakeup: false,
      oneKnob: false,
      oneKnobLevel: 0,
    },
    ssmcs: SSMCS_INITIAL,
    insertFx: -1,
    insertFxOn: false,
    stereoLink: false,
    panBal: 0,
  },
  ch4: {
    on: true,
    hpf: false,
    hpfFreq: 80,
    compEqType: 0,
    recPoint: 4,
    phase: false,
    gateOn: false,
    compOn: false,
    eqOn: true,
    eqOneKnob: { on: false, type: 0, level: 0 },
    eqBands: [
      { on: true, q: 0.71, freq: 125, gain: 0, type: 1 },
      { on: true, q: 0.71, freq: 1000, gain: 0 },
      { on: true, q: 0.71, freq: 4000, gain: 0 },
      { on: true, q: 0.71, freq: 10000, gain: 0, type: 1 },
    ],
    gate: { threshold: -50, range: -56, attack: 20.17, hold: 15.3, decay: 150.2 },
    comp: {
      threshold: -18,
      ratio: 3,
      gain: 2,
      attack: 34.58,
      release: 218,
      knee: 1,
      autoMakeup: false,
      oneKnob: false,
      oneKnobLevel: 0,
    },
    ssmcs: SSMCS_INITIAL,
    insertFx: -1,
    insertFxOn: false,
  },
  ch_5_6: {
    on: true,
    recPoint: 4,
    phaseL: false,
    phaseR: false,
    eqOn: true,
    eqOneKnob: { on: false, type: 0, level: 0 },
    eqBands: [
      { on: true, q: 0.71, freq: 125, gain: 0, type: 1 },
      { on: true, q: 0.71, freq: 1000, gain: 0 },
      { on: true, q: 0.71, freq: 4000, gain: 0 },
      { on: true, q: 0.71, freq: 10000, gain: 0, type: 1 },
    ],
  },
  ch_7_8: {
    on: true,
    recPoint: 4,
    phaseL: false,
    phaseR: false,
    eqOn: true,
    eqOneKnob: { on: false, type: 0, level: 0 },
    eqBands: [
      { on: true, q: 0.71, freq: 125, gain: 0, type: 1 },
      { on: true, q: 0.71, freq: 1000, gain: 0 },
      { on: true, q: 0.71, freq: 4000, gain: 0 },
      { on: true, q: 0.71, freq: 10000, gain: 0, type: 1 },
    ],
  },
  ch_9_10: {
    on: true,
    recPoint: 4,
    phaseL: false,
    phaseR: false,
    eqOn: true,
    eqOneKnob: { on: false, type: 0, level: 0 },
    eqBands: [
      { on: true, q: 0.71, freq: 125, gain: 0, type: 1 },
      { on: true, q: 0.71, freq: 1000, gain: 0 },
      { on: true, q: 0.71, freq: 4000, gain: 0 },
      { on: true, q: 0.71, freq: 10000, gain: 0, type: 1 },
    ],
  },
  ch_11_12: {
    on: true,
    recPoint: 4,
    phaseL: false,
    phaseR: false,
    eqOn: true,
    eqOneKnob: { on: false, type: 0, level: 0 },
    eqBands: [
      { on: true, q: 0.71, freq: 125, gain: 0, type: 1 },
      { on: true, q: 0.71, freq: 1000, gain: 0 },
      { on: true, q: 0.71, freq: 4000, gain: 0 },
      { on: true, q: 0.71, freq: 10000, gain: 0, type: 1 },
    ],
  },
  "bus.stereo": {
    level: 0,
    pan: 0,
    insertFx: -1,
    insertFxOn: false,
    eqOn: true,
    eqOneKnob: { on: false, type: 0, level: 0 },
    eqBands: [
      { on: true, q: 0.71, freq: 125, gain: 0, type: 1 },
      { on: true, q: 0.71, freq: 1000, gain: 0 },
      { on: true, q: 0.71, freq: 4000, gain: 0 },
      { on: true, q: 0.71, freq: 10000, gain: 0, type: 1 },
    ],
    on: true,
  },
  "bus.mix1": {
    level: 0,
    pan: 0,
    busType: 0,
    insertFx: -1,
    insertFxOn: false,
    eqOn: true,
    eqOneKnob: { on: false, type: 0, level: 0 },
    eqBands: [
      { on: true, q: 0.71, freq: 125, gain: 0, type: 1 },
      { on: true, q: 0.71, freq: 1000, gain: 0 },
      { on: true, q: 0.71, freq: 4000, gain: 0 },
      { on: true, q: 0.71, freq: 10000, gain: 0, type: 1 },
    ],
    on: true,
    panLink: false,
  },
  "bus.mix2": {
    level: 0,
    pan: 0,
    busType: 0,
    insertFx: -1,
    insertFxOn: false,
    eqOn: true,
    eqOneKnob: { on: false, type: 0, level: 0 },
    eqBands: [
      { on: true, q: 0.71, freq: 125, gain: 0, type: 1 },
      { on: true, q: 0.71, freq: 1000, gain: 0 },
      { on: true, q: 0.71, freq: 4000, gain: 0 },
      { on: true, q: 0.71, freq: 10000, gain: 0, type: 1 },
    ],
    on: true,
    panLink: false,
  },
  // FX channels ship ON at the factory (param 338, def 1) with their factory
  // effect program loaded — FX1 = Rev-X Hall (type 0), FX2 = Mono Delay (type
  // 1024) — captured from the device. Seeded so a fresh plan restores both the ON
  // state and the effect (the absolute write only auto-clears wires, not scalar
  // node params — see bus.stereo/osc/monitor).
  "bus.fx1": {
    on: true,
    fxEffect: {
      type: 0,
      params: {
        reverbTime: 23,
        revxInitialDelay: 2,
        decay: 27,
        roomSize: 29,
        revxDiffusion: 10,
        revxHpf: 4,
        revxLpf: 50,
        revxHiRatio: 8,
        lowRatio: 12,
        lowFreq: 32,
      },
    },
  },
  "bus.fx2": {
    on: true,
    fxEffect: {
      type: 1024,
      params: {
        delay: 5000,
        delayFeedback: 20,
        delayHiRatio: 7,
        delayHpf: 40,
        delayLpf: 110,
        sync: 0,
        bpm: 120,
        note: 9,
      },
    },
  },
  "out.ducker1": {
    duckerOn: false,
    ducker: { threshold: -40, range: -24, attack: 20.17, decay: 1000 },
  },
  "out.ducker2": {
    duckerOn: false,
    ducker: { threshold: -40, range: -24, attack: 20.17, decay: 1000 },
  },
  "out.ducker3": {
    duckerOn: false,
    ducker: { threshold: -40, range: -24, attack: 20.17, decay: 1000 },
  },
  "out.ducker4": {
    duckerOn: false,
    ducker: { threshold: -40, range: -24, attack: 20.17, decay: 1000 },
  },
  "bus.mon1": { level: 0, cueInterrupt: true, mono: false, phonesLevel: 2, on: true },
  "bus.mon2": { level: 0, cueInterrupt: true, mono: false, phonesLevel: 2, on: true },
  "bus.osc": {
    osc: { on: false, level: -14, mode: 0, freq: 1000, width: 0.1, interval: 1 },
  },
  // STREAMING DELAY factory state, read from the device: off, 1.00 ms, 30 fps.
  // Seeded so writing a fresh plan restores the device's delay (the absolute
  // write only auto-clears wires, not scalar node params — see bus.osc/monitor).
  "bus.stream": { delay: { on: false, time: 1, frameRate: 5 } },
  // microSD Rec Track Count factory state: 16 tracks (never written to the device,
  // so this only drives how many track-pair slots the canvas shows; at 16 all 8 pair
  // slots are active, so the seeded STEREO assign on track 15/16 shows).
  "out.sdrec": { sdRecTrackCount: 16 },
};

// Factory CH SETTING colors, read from the device: input channels and the FX
// buses ship Blue, the MIX and STREAMING buses Orange, the STEREO master Red. Hex
// are the COLOR_PALETTE entries (Blue = index 0, Orange = 1, Red = 6); kept as
// literals so this data file does not depend on the control layer.
export const URX44V_NODE_COLORS: Record<string, string> = {
  ch1: "#4a78c0",
  ch2: "#4a78c0",
  ch3: "#4a78c0",
  ch4: "#4a78c0",
  ch_5_6: "#4a78c0",
  ch_7_8: "#4a78c0",
  ch_9_10: "#4a78c0",
  ch_11_12: "#4a78c0",
  "bus.fx1": "#4a78c0",
  "bus.fx2": "#4a78c0",
  "bus.mix1": "#e8913a",
  "bus.mix2": "#e8913a",
  "bus.stream": "#e8913a",
  "bus.stereo": "#d9534f",
};

// Factory CH SETTING names, read from the device (mono channels from param 18,
// stereo channels from param 206, buses from their own params), trimmed to match
// readback. Mono ship "ch 1".."ch 4", stereo the pair label " 5/ 6"…, buses their
// short labels. The device right-aligns each number in a 2-character field, so
// single-digit numbers carry a leading space (" 5/ 6", not "5/ 6"); verified
// against a factory-reset unit's own settings backup (param 206).
export const URX44V_NODE_NAMES: Record<string, string> = {
  ch1: "ch 1",
  ch2: "ch 2",
  ch3: "ch 3",
  ch4: "ch 4",
  ch_5_6: " 5/ 6",
  ch_7_8: " 7/ 8",
  ch_9_10: " 9/10",
  ch_11_12: "11/12",
  "bus.fx1": "FX 1",
  "bus.fx2": "FX 2",
  "bus.mix1": "MIX1",
  "bus.mix2": "MIX2",
  "bus.stream": "Strm",
  "bus.stereo": "ST",
};

export const URX44V_CONNECTIONS: PlanConnection[] = [
  {
    from: "ch1:out",
    to: "bus.stereo:in",
    kind: "send",
    params: { level: 0, pan: 0 },
  },
  {
    from: "ch2:out",
    to: "bus.stereo:in",
    kind: "send",
    params: { level: 0, pan: 0 },
  },
  {
    from: "ch3:out",
    to: "bus.stereo:in",
    kind: "send",
    params: { level: 0, pan: 0 },
  },
  {
    from: "ch4:out",
    to: "bus.stereo:in",
    kind: "send",
    params: { level: 0, pan: 0 },
  },
  {
    from: "ch_5_6:out",
    to: "bus.stereo:in",
    kind: "send",
    params: { level: 0, pan: 0 },
  },
  {
    from: "ch_7_8:out",
    to: "bus.stereo:in",
    kind: "send",
    params: { level: 0, pan: 0 },
  },
  {
    from: "ch_9_10:out",
    to: "bus.stereo:in",
    kind: "send",
    params: { level: 0, pan: 0 },
  },
  {
    from: "ch_11_12:out",
    to: "bus.stereo:in",
    kind: "send",
    params: { level: 0, pan: 0 },
  },
  {
    from: "bus.fx1:out",
    to: "bus.stereo:in",
    kind: "send",
    params: { level: -96.5 },
  },
  {
    from: "bus.fx2:out",
    to: "bus.stereo:in",
    kind: "send",
    params: { level: -96.5 },
  },
  // FX channel → MIX sends ship ON at the factory (SEND_ON = 1) but at -∞, so they
  // are seeded as connections (a wire = on) carrying the -∞ off-level / center BAL.
  {
    from: "bus.fx1:out",
    to: "bus.mix1:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "bus.fx1:out",
    to: "bus.mix2:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "bus.fx2:out",
    to: "bus.mix1:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "bus.fx2:out",
    to: "bus.mix2:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch1:out",
    to: "bus.mix1:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch1:out",
    to: "bus.mix2:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch1:out",
    to: "bus.fx1:in",
    kind: "send",
    params: { level: -96.5, tap: "post" },
  },
  {
    from: "ch1:out",
    to: "bus.fx2:in",
    kind: "send",
    params: { level: -96.5, tap: "post" },
  },
  {
    from: "ch2:out",
    to: "bus.mix1:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch2:out",
    to: "bus.mix2:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch2:out",
    to: "bus.fx1:in",
    kind: "send",
    params: { level: -96.5, tap: "post" },
  },
  {
    from: "ch2:out",
    to: "bus.fx2:in",
    kind: "send",
    params: { level: -96.5, tap: "post" },
  },
  {
    from: "ch3:out",
    to: "bus.mix1:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch3:out",
    to: "bus.mix2:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch3:out",
    to: "bus.fx1:in",
    kind: "send",
    params: { level: -96.5, tap: "post" },
  },
  {
    from: "ch3:out",
    to: "bus.fx2:in",
    kind: "send",
    params: { level: -96.5, tap: "post" },
  },
  {
    from: "ch4:out",
    to: "bus.mix1:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch4:out",
    to: "bus.mix2:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch4:out",
    to: "bus.fx1:in",
    kind: "send",
    params: { level: -96.5, tap: "post" },
  },
  {
    from: "ch4:out",
    to: "bus.fx2:in",
    kind: "send",
    params: { level: -96.5, tap: "post" },
  },
  {
    from: "ch_5_6:out",
    to: "bus.mix1:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch_5_6:out",
    to: "bus.mix2:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch_5_6:out",
    to: "bus.fx1:in",
    kind: "send",
    params: { level: -96.5, tap: "post" },
  },
  {
    from: "ch_5_6:out",
    to: "bus.fx2:in",
    kind: "send",
    params: { level: -96.5, tap: "post" },
  },
  {
    from: "ch_7_8:out",
    to: "bus.mix1:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch_7_8:out",
    to: "bus.mix2:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch_7_8:out",
    to: "bus.fx1:in",
    kind: "send",
    params: { level: -96.5, tap: "post" },
  },
  {
    from: "ch_7_8:out",
    to: "bus.fx2:in",
    kind: "send",
    params: { level: -96.5, tap: "post" },
  },
  {
    from: "ch_9_10:out",
    to: "bus.mix1:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch_9_10:out",
    to: "bus.mix2:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch_9_10:out",
    to: "bus.fx1:in",
    kind: "send",
    params: { level: -96.5, tap: "post" },
  },
  {
    from: "ch_9_10:out",
    to: "bus.fx2:in",
    kind: "send",
    params: { level: -96.5, tap: "post" },
  },
  {
    from: "ch_11_12:out",
    to: "bus.mix1:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch_11_12:out",
    to: "bus.mix2:in",
    kind: "send",
    params: { level: -96.5, pan: 0, tap: "post" },
  },
  {
    from: "ch_11_12:out",
    to: "bus.fx1:in",
    kind: "send",
    params: { level: -96.5, tap: "post" },
  },
  {
    from: "ch_11_12:out",
    to: "bus.fx2:in",
    kind: "send",
    params: { level: -96.5, tap: "post" },
  },
  { from: "ch1:out", to: "out.ducker1:in", kind: "key" },
  { from: "ch1:out", to: "out.ducker2:in", kind: "key" },
  { from: "ch1:out", to: "out.ducker3:in", kind: "key" },
  { from: "ch1:out", to: "out.ducker4:in", kind: "key" },
  {
    from: "bus.osc:out",
    to: "bus.stereo:in",
    kind: "sendSwitch",
    params: { oscL: true, oscR: true },
  },
  { from: "in.micline_1_2:out", to: "ch1:in", kind: "source" },
  { from: "in.micline_1_2:out", to: "ch2:in", kind: "source" },
  { from: "in.micline_3_4:out", to: "ch3:in", kind: "source" },
  { from: "in.micline_3_4:out", to: "ch4:in", kind: "source" },
  // Stereo channels carry their source on param 209/210 (not param 22), so the
  // factory defaults read back as CH5/6=AUX, CH7/8=USB MAIN A, CH9/10=USB MAIN B,
  // CH11/12=USB MAIN C (confirmed on URX44V).
  { from: "in.aux:out", to: "ch_5_6:in", kind: "source" },
  { from: "in.usbmain_a:out", to: "ch_7_8:in", kind: "source" },
  { from: "in.usbmain_b:out", to: "ch_9_10:in", kind: "source" },
  { from: "in.usbmain_c:out", to: "ch_11_12:in", kind: "source" },
  { from: "bus.stereo:out", to: "bus.stream:in", kind: "source" },
  { from: "bus.stereo:out", to: "out.usbmain_a:in", kind: "patch" },
  { from: "bus.stereo:out", to: "out.usbmain_b:in", kind: "patch" },
  { from: "bus.stereo:out", to: "out.usbmain_c:in", kind: "patch" },
  { from: "bus.stereo:out", to: "out.usbsub:in", kind: "patch" },
  { from: "bus.stereo:out", to: "bus.mon1:in", kind: "source" },
  { from: "bus.stereo:out", to: "bus.mon2:in", kind: "source" },
  { from: "bus.stereo:out", to: "out.main:in", kind: "patch" },
  { from: "bus.mix1:out", to: "out.line:in", kind: "patch" },
  // microSD Rec factory track assign (param 736, confirmed on URX44V): tracks 1-12
  // = CH1-12 (each pair from its primary channel node), tracks 13/14 = none (no
  // wire), tracks 15/16 = STEREO. Track Count 16 (out.sdrec.sdRecTrackCount).
  { from: "ch1:out", to: "out.sdrec.t1:in", kind: "record" },
  { from: "ch3:out", to: "out.sdrec.t2:in", kind: "record" },
  { from: "ch_5_6:out", to: "out.sdrec.t3:in", kind: "record" },
  { from: "ch_7_8:out", to: "out.sdrec.t4:in", kind: "record" },
  { from: "ch_9_10:out", to: "out.sdrec.t5:in", kind: "record" },
  { from: "ch_11_12:out", to: "out.sdrec.t6:in", kind: "record" },
  { from: "bus.stereo:out", to: "out.sdrec.t8:in", kind: "record" },
];
